import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { useContactsListQuery, useProjectsBoardQuery, useProjectsAllQuery, useProjectsPmMissingQuery, useProjectStatusesQuery, usePortalInvalidate, usePrefetchDetailOnHover } from "@/hooks/usePortalQueries";
import { SyncPill } from "@/components/SyncPill";
import { RecordFormModal } from "@/components/RecordFormModal";
import { ActivityBadge } from "@/components/ActivityBadge";
import { EmptyState } from "@/components/EmptyState";
import { TableSkeleton, CardGridSkeleton } from "@/components/Skeletons";
import { Switch } from "@/components/ui/switch";
import { formatMoney } from "@/lib/money";
import { Mail, Plus, Search, Trash2, Loader2, Star, Users2, AlertCircle, X, Check } from "lucide-react";
import { toast } from "sonner";

// Batch Item 4 (2026-02): pairwise merge review — pick a survivor within a duplicate group,
// merge one other member into it at a time. Non-conflicting fields auto-resolve; a conflict
// (both sides have a different, non-empty value) needs an explicit pick before "Merge" enables.
function MergeReviewModal({ survivorId, redundantId, onClose, onMerged }) {
  const [diff, setDiff] = useState(null);
  const [survivor, setSurvivor] = useState(null);
  const [redundant, setRedundant] = useState(null);
  const [choices, setChoices] = useState({});
  const [merging, setMerging] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const loadPreview = () => {
    setLoadError(null);
    setDiff(null);
    api.get("/v1/contacts/merge-preview", { params: { survivor_id: survivorId, redundant_id: redundantId } }).then((r) => {
      setDiff(r.data.diff); setSurvivor(r.data.survivor); setRedundant(r.data.redundant);
      const initial = {};
      Object.entries(r.data.diff).forEach(([k, v]) => { if (v.status === "conflict") initial[k] = "survivor"; });
      setChoices(initial);
    }).catch(() => setLoadError("Couldn't load these contacts — one may have already been merged or archived."));
  };
  // FIX 2 (2026-02): this fetch had no .catch() at all — any failure (404 on a stale duplicate
  // pair, network hiccup) left `diff` null forever, which the render below reads as "still
  // loading", so the modal hung on "Loading…" indefinitely with no way out but Cancel. Now
  // surfaces a real error state with a Retry action instead.
  useEffect(loadPreview, [survivorId, redundantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const conflictFields = diff ? Object.entries(diff).filter(([, v]) => v.status === "conflict") : [];

  const doMerge = async () => {
    setMerging(true);
    try {
      const resolved_fields = {};
      conflictFields.forEach(([k, v]) => { resolved_fields[k] = choices[k] === "redundant" ? v.redundant : v.survivor; });
      await api.post("/v1/contacts/merge", { survivor_id: survivorId, redundant_id: redundantId, resolved_fields });
      toast.success(`Merged "${redundant?.name}" into "${survivor?.name}"`);
      onMerged();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code === "unresolved_conflicts" ? "Please resolve every conflicting field" : "Couldn't merge contacts");
    } finally {
      setMerging(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" data-testid="merge-review-modal">
      <div className="w-full max-w-lg rounded-lg bg-[#FFFFFF] p-5 shadow-2xl max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm text-[#1C1C1E]">{survivor || redundant ? `Merge "${redundant?.name}" into "${survivor?.name}"` : "Merge Contacts"}</h3>
          <button data-testid="merge-review-close-btn" onClick={onClose}><X className="h-4 w-4 text-[#A8A29E] hover:text-[#1C1C1E]" /></button>
        </div>
        {loadError ? (
          <div className="py-6 text-center" data-testid="merge-review-error">
            <div className="text-xs text-red-600 mb-3">{loadError}</div>
            <button onClick={loadPreview} data-testid="merge-review-retry-btn"
              className="rounded-md border border-[#E7E5E4] px-3 py-1.5 text-xs text-[#57534E] hover:border-[#FFC817] hover:text-[#B45309]">Retry</button>
          </div>
        ) : !diff ? <div className="text-xs text-[#A8A29E] py-6 text-center" data-testid="merge-review-loading">Loading…</div> : (
          <div className="space-y-2">
            {Object.entries(diff).map(([field, v]) => (
              <div key={field} className={`rounded-md border px-3 py-2 ${v.status === "conflict" ? "border-orange-400/40 bg-orange-500/5" : "border-[#E7E5E4]"}`} data-testid={`merge-diff-field-${field}`}>
                <div className="text-[10px] uppercase tracking-wider text-[#A8A29E] mb-1 flex items-center gap-1.5">
                  {field.replace(/_/g, " ")}
                  {v.status === "conflict" && <span className="inline-flex items-center gap-1 text-orange-600"><AlertCircle className="h-3 w-3" /> needs a pick</span>}
                </div>
                {v.status === "same" ? (
                  <div className="text-xs text-[#57534E]">{v.survivor || "—"} <span className="text-[#A8A29E]">(matches)</span></div>
                ) : v.status !== "conflict" ? (
                  <div className="text-xs text-[#1C1C1E]">{v.survivor || v.redundant || "—"}</div>
                ) : (
                  <div className="flex flex-col gap-1.5">
                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                      <input type="radio" name={`choice-${field}`} data-testid={`merge-choice-${field}-survivor`} checked={choices[field] !== "redundant"}
                        onChange={() => setChoices((prev) => ({ ...prev, [field]: "survivor" }))} className="accent-[#FFC817]" />
                      {v.survivor}
                    </label>
                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                      <input type="radio" name={`choice-${field}`} data-testid={`merge-choice-${field}-redundant`} checked={choices[field] === "redundant"}
                        onChange={() => setChoices((prev) => ({ ...prev, [field]: "redundant" }))} className="accent-[#FFC817]" />
                      {v.redundant}
                    </label>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button data-testid="merge-review-cancel-btn" onClick={onClose} className="rounded-md px-3 py-1.5 text-xs text-[#57534E] hover:text-[#1C1C1E]">Cancel</button>
          <button data-testid="merge-review-confirm-btn" onClick={doMerge} disabled={!diff || merging}
            className="inline-flex items-center gap-1.5 rounded-md bg-[#FFC817] px-3 py-1.5 text-xs font-medium text-[#1C1C1E] hover:bg-[#E6B415] disabled:opacity-50">
            {merging ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Merge
          </button>
        </div>
      </div>
    </div>
  );
}

function DuplicatesModal({ onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [survivorByGroup, setSurvivorByGroup] = useState({});
  const [mergePair, setMergePair] = useState(null); // {survivorId, redundantId}

  const load = () => { setLoadError(null); api.get("/v1/contacts/duplicates").then((r) => setData(r.data)).catch(() => setLoadError("Couldn't load duplicates.")); };
  useEffect(() => { load(); }, []);

  const groups = data ? [
    ...data.confirmed.map((g) => ({ ...g, kind: "confirmed" })),
    ...data.possible.map((g) => ({ ...g, kind: "possible" })),
  ] : [];

  const survivorFor = (group, key) => {
    if (survivorByGroup[key]) return survivorByGroup[key];
    // default survivor = the most complete record (fewest missing fields)
    return [...group.contacts].sort((a, b) => (a.missing_fields?.length || 0) - (b.missing_fields?.length || 0))[0].id;
  };

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/40 p-4" data-testid="duplicates-modal">
      <div className="w-full max-w-2xl rounded-lg bg-[#FFFFFF] p-5 shadow-2xl max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-editorial text-2xl text-[#1C1C1E]">Possible Duplicates</h3>
          <button data-testid="duplicates-close-btn" onClick={onClose}><X className="h-4 w-4 text-[#A8A29E] hover:text-[#1C1C1E]" /></button>
        </div>
        <p className="text-xs text-[#A8A29E] mb-4">Confirmed = same email. Possible = same name + shared client or email domain.</p>
        {loadError ? (
          <div className="py-6 text-center" data-testid="duplicates-error">
            <div className="text-xs text-red-600 mb-3">{loadError}</div>
            <button onClick={load} data-testid="duplicates-retry-btn" className="rounded-md border border-[#E7E5E4] px-3 py-1.5 text-xs text-[#57534E] hover:border-[#FFC817] hover:text-[#B45309]">Retry</button>
          </div>
        ) : !data ? <div className="text-xs text-[#A8A29E] py-6 text-center">Loading…</div> : groups.length === 0 ? (
          <EmptyState title="No duplicates found" hint="Contacts look clean." testId="duplicates-empty" />
        ) : (
          <div className="space-y-4">
            {groups.map((g, idx) => {
              const key = `${g.kind}-${idx}`;
              const survivorId = survivorFor(g, key);
              return (
                <div key={key} className="rounded-md border border-[#E7E5E4] overflow-hidden" data-testid={`duplicate-group-${key}`}>
                  <header className={`px-3 py-1.5 text-[10px] uppercase tracking-wider ${g.kind === "confirmed" ? "bg-red-500/10 text-red-600" : "bg-orange-500/10 text-orange-600"}`}>
                    {g.kind === "confirmed" ? "Confirmed — same email" : "Possible"} · {g.contacts.length} contacts
                  </header>
                  <div className="divide-y divide-[#E7E5E4]">
                    {g.contacts.map((c) => (
                      <div key={c.id} className="flex items-center justify-between gap-3 px-3 py-2" data-testid={`duplicate-member-${c.id}`}>
                        <label className="flex items-center gap-2 min-w-0 cursor-pointer">
                          <input type="radio" name={`survivor-${key}`} checked={survivorId === c.id} data-testid={`survivor-radio-${c.id}`}
                            onChange={() => setSurvivorByGroup((prev) => ({ ...prev, [key]: c.id }))} className="accent-[#FFC817] shrink-0" />
                          <div className="min-w-0">
                            <div className="text-xs text-[#1C1C1E] truncate">{c.name} {survivorId === c.id && <span className="text-[9px] text-[#B45309] uppercase tracking-wider ml-1">keep</span>}</div>
                            <div className="text-[10px] text-[#A8A29E] truncate">{c.email || "no email"} · {c.client_name || "no client"}</div>
                          </div>
                        </label>
                        {survivorId !== c.id && (
                          <button data-testid={`merge-into-survivor-btn-${c.id}`} onClick={() => setMergePair({ survivorId, redundantId: c.id })}
                            className="shrink-0 rounded-md border border-[#E7E5E4] px-2 py-1 text-[11px] text-[#57534E] hover:border-[#FFC817] hover:text-[#B45309]">
                            Merge into "keep"
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {mergePair && (
        <MergeReviewModal survivorId={mergePair.survivorId} redundantId={mergePair.redundantId}
          onClose={() => setMergePair(null)}
          onMerged={() => { setMergePair(null); load(); onChanged(); }} />
      )}
    </div>
  );
}

export function Contacts() {
  const { hasScope } = useAuth();
  const navigate = useNavigate();
  const [showNew, setShowNew] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [query, setQuery] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [missingOnly, setMissingOnly] = useState(false);
  const [groupByClient, setGroupByClient] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [dupeCount, setDupeCount] = useState(0);
  const invalidatePortal = usePortalInvalidate();

  // Wave 1 (2026-10, frontend data cache) — contacts list cached 60s (Notion-synced data);
  // `load()` now just invalidates, and since this page keeps the query mounted, that triggers
  // an immediate background refetch (same visible effect as the old manual reload).
  const { data: items = [], isLoading: loading, isError, error: queryError } = useContactsListQuery(includeArchived);
  const load = () => invalidatePortal.contacts();
  const loadDupeCount = () => api.get("/v1/contacts/duplicates").then((r) => setDupeCount(r.data.confirmed.length + r.data.possible.length));
  useEffect(() => { loadDupeCount(); }, [includeArchived]);

  // F (2026-02): search by name/email, client-side — items already arrive A-Z sorted from the
  // backend, so an empty query preserves that order exactly.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((c) => {
      if (q && !(c.name || "").toLowerCase().includes(q) && !(c.email || "").toLowerCase().includes(q)) return false;
      if (clientFilter && c.client_id !== clientFilter) return false;
      if (missingOnly && (!c.missing_fields || c.missing_fields.length === 0)) return false;
      return true;
    });
  }, [items, query, clientFilter, missingOnly]);

  const clientOptions = useMemo(() => {
    const seen = new Map();
    items.forEach((c) => { if (c.client_id && !seen.has(c.client_id)) seen.set(c.client_id, c.client_name); });
    return Array.from(seen.entries()).sort((a, b) => (a[1] || "").localeCompare(b[1] || ""));
  }, [items]);

  const grouped = useMemo(() => {
    if (!groupByClient) return null;
    const byClient = new Map();
    filtered.forEach((c) => {
      const key = c.client_id || "__none__";
      if (!byClient.has(key)) byClient.set(key, { name: c.client_name || "No client", items: [] });
      byClient.get(key).items.push(c);
    });
    return Array.from(byClient.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [filtered, groupByClient]);

  const togglePrimary = async (c) => {
    if (!c.client_id) { toast.error("Assign this contact to a client first"); return; }
    try {
      if (c.is_primary_contact) await api.post(`/v1/contacts/${c.id}/unset-primary`, { client_id: c.client_id });
      else await api.post(`/v1/contacts/${c.id}/set-primary`, { client_id: c.client_id });
      load();
    } catch {
      toast.error("Couldn't update primary contact");
    }
  };

  const Row = (c) => (
    <tr key={c.id} data-testid={`contact-row-${c.id}`} className={`border-b border-hairline last:border-b-0 hover:bg-surface2 ${c.archived ? "opacity-60" : ""}`}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          {hasScope("contacts.create") && c.client_id && (
            <button type="button" data-testid={`primary-contact-toggle-${c.id}`} onClick={() => togglePrimary(c)} title={c.is_primary_contact ? "Primary contact — click to unset" : "Set as primary contact"}>
              <Star className={`h-3.5 w-3.5 ${c.is_primary_contact ? "fill-brand text-brand" : "text-hairline-strong hover:text-brand"}`} />
            </button>
          )}
          <Link to={`/contacts/${c.id}`} data-testid={`contact-row-link-${c.id}`} className="text-ink hover:text-gold-text">{c.name}</Link>
          {c.archived && <span className="rounded-full bg-tone-grey-bg px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-tone-grey-text" data-testid={`contact-archived-badge-${c.id}`}>Archived</span>}
          {c.missing_fields?.length > 0 && (
            <span title={`Missing: ${c.missing_fields.join(", ")}`} className="rounded-full bg-surface2 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-ink-soft" data-testid={`contact-incomplete-badge-${c.id}`}>Incomplete</span>
          )}
        </div>
      </td>
      {!groupByClient && <td className="px-4 py-3 text-xs text-ink-soft">{c.client_name || "—"}</td>}
      <td className="px-4 py-3 text-ink-soft">{c.title || c.role || c.role_title}</td>
      <td className="px-4 py-3 font-mono text-xs text-ink-soft">{c.email}</td>
      <td className="px-4 py-3"><ActivityBadge iso={c.last_activity_at} testId={`contact-activity-${c.id}`} /></td>
      <td className="px-4 py-3 text-right"><SyncPill state={c.sync?.state} compact /></td>
    </tr>
  );

  return (
    <div className="p-8" data-testid="contacts-page">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <h1 className="font-editorial text-4xl tracking-tight text-ink">Contacts</h1>
        <div className="flex items-center gap-3 flex-wrap">
          {dupeCount > 0 && (
            <button type="button" data-testid="possible-duplicates-btn" onClick={() => setShowDuplicates(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-tone-gold-text/30 bg-tone-gold-bg px-2.5 py-1.5 text-xs text-tone-gold-text hover:bg-tone-gold-bg/70">
              <Users2 className="h-3.5 w-3.5" /> Possible Duplicates ({dupeCount})
            </button>
          )}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-faint" />
            <input data-testid="contacts-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name or email…"
              className="w-56 rounded-md border border-hairline bg-paper pl-8 pr-3 py-1.5 text-xs text-ink placeholder:text-faint focus:outline-none focus:border-brand" />
          </div>
          <select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} data-testid="contacts-client-filter"
            className="rounded-md border border-hairline bg-paper px-2 py-1.5 text-xs text-ink">
            <option value="">All clients</option>
            {clientOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
          <label className="inline-flex items-center gap-2 text-xs text-ink-soft">
            <Switch checked={missingOnly} onCheckedChange={setMissingOnly} data-testid="contacts-missing-only-toggle" />
            Missing info
          </label>
          <label className="inline-flex items-center gap-2 text-xs text-ink-soft">
            <Switch checked={groupByClient} onCheckedChange={setGroupByClient} data-testid="contacts-group-by-client-toggle" />
            Group by client
          </label>
          <label className="inline-flex items-center gap-2 text-xs text-ink-soft">
            <Switch checked={includeArchived} onCheckedChange={setIncludeArchived} data-testid="contacts-show-archived-toggle" />
            Show archived
          </label>
          {hasScope("contacts.create") && (
            <button type="button" data-testid="new-contact-btn" onClick={() => setShowNew(true)}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-ink hover:bg-brand-dim">
              <Plus className="h-3.5 w-3.5" /> New Contact
            </button>
          )}
        </div>
      </div>
      <RecordFormModal type="contact" open={showNew} onClose={() => setShowNew(false)}
        onCreated={(record, opts) => { load(); if (opts?.openDetail && record?.id) navigate(`/contacts/${record.id}`); }} />
      {showDuplicates && <DuplicatesModal onClose={() => setShowDuplicates(false)} onChanged={() => { load(); loadDupeCount(); }} />}
      {isError && items.length === 0 ? (
        <div className="rounded-lg border border-tone-red-text/20 bg-tone-red-bg p-8 text-center text-sm text-tone-red-text" data-testid="contacts-error-state">
          {queryError?.response?.status === 403 ? "Access Restricted" : "Couldn't load contacts."}
          <button type="button" onClick={() => load()} data-testid="contacts-error-retry" className="ml-2 font-semibold underline">Retry</button>
        </div>
      ) : loading ? <TableSkeleton cols={5} testId="contacts-skeleton" /> : (
      <div className="rounded-lg border border-hairline bg-paper overflow-hidden">
        {groupByClient && grouped ? (
          grouped.map((g) => (
            <div key={g.name}>
              <div className="bg-canvas px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-faint border-b border-hairline" data-testid={`contact-group-header-${g.name}`}>{g.name} ({g.items.length})</div>
              <table className="w-full text-sm">
                <tbody>{g.items.map(Row)}</tbody>
              </table>
            </div>
          ))
        ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-hairline text-[10px] font-semibold uppercase tracking-wider text-faint">
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Client</th>
              <th className="text-left px-4 py-3">Title</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Activity</th>
              <th className="text-right px-4 py-3">Sync</th>
            </tr>
          </thead>
          <tbody>{filtered.map(Row)}</tbody>
        </table>
        )}
        {filtered.length === 0 && (
          <EmptyState title={query ? "No contacts match your search" : "No contacts yet"} hint={query ? "Try a different name or email." : "Add a contact to get started."} testId="contacts-empty"
            actionLabel={!query && hasScope("contacts.create") ? "+ New Contact" : undefined} onAction={!query && hasScope("contacts.create") ? () => setShowNew(true) : undefined} />
        )}
      </div>
      )}
    </div>
  );
}

// Batch Item 2 (2026-02): "Delivered"/"Archived" are the two Notion statuses the founder
// called out as the bulk of the noise (~131 + ~23 of ~163 total) — collapsed to a count
// header by default, lazy-loaded/paginated on demand via /board/lazy. Everything else is
// "active" and loads fully, lightweight, on open via /board.
const DEFERRED_STATUSES = ["Delivered", "Archived"];

// Phase 4 (2026-10, Design System 2.0 visual reskin) — denser board card: tighter padding/
// gaps, tokens instead of hardcoded hex, same fields/data/links as before.
function ProjectCard({ p, canBulkDelete, selected, onToggleSelect, onOpen, prefetchProps }) {
  return (
    <div data-testid={`project-card-${p.id}`} onClick={() => onOpen(p.id)} {...(prefetchProps ? prefetchProps(p.id) : {})}
      className={`relative rounded border border-hairline bg-paper p-2.5 hover:border-brand/40 cursor-pointer transition-colors ${p.archived ? "opacity-60" : ""} ${selected ? "ring-2 ring-brand" : ""}`}>
      <div className="text-[12.5px] text-ink mb-0.5 flex items-center gap-1.5">
        {canBulkDelete && (
          <input type="checkbox" data-testid={`project-select-${p.id}`} checked={selected} onChange={(e) => onToggleSelect(p.id, e)} onClick={(e) => e.stopPropagation()} className="h-3.5 w-3.5 accent-brand shrink-0" />
        )}
        <span className="flex-1 truncate font-medium">{p.name}</span>
        {p.archived && <span className="rounded-full bg-tone-gold-bg px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-tone-gold-text shrink-0" data-testid={`project-archived-badge-${p.id}`}>Archived</span>}
      </div>
      {p.client_name && <div className="text-[10px] text-faint truncate mb-1">{p.client_name}</div>}
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1 rounded-full bg-hairline overflow-hidden">
          <div className="h-full bg-brand" style={{ width: `${(p.progress || 0) * 100}%` }} />
        </div>
        <SyncPill state={p.sync?.state} compact />
      </div>
      <div className="mt-1.5 flex items-center justify-between">
        {p.budget != null && <div className="text-[10px] font-mono text-faint" data-testid={`project-budget-${p.id}`}>{formatMoney(p.budget, p.currency)} budget</div>}
        <ActivityBadge iso={p.last_activity_at} testId={`project-activity-${p.id}`} />
      </div>
      <Link to={`/commercial?project_id=${p.id}`} data-testid={`project-draft-email-${p.id}`} onClick={(e) => e.stopPropagation()}
        className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-faint hover:text-gold-text">
        <Mail className="h-2.5 w-2.5" /> Draft email
      </Link>
    </div>
  );
}

// Phase 4 (2026-10): fixed-width column (232px standard, 168px for collapsed Delivered/
// Archived) so the board reads as ONE horizontal row + scroll, not a wrapping grid.
function ProjectColumn({ status, items, canBulkDelete, selected, onToggleSelect, onOpen, footer, width = 232, prefetchProps }) {
  return (
    <div className="shrink-0 rounded-md border border-hairline bg-paper" style={{ width }} data-testid={`project-column-${status}`}>
      <header className="border-b border-hairline px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-faint flex justify-between">
        <span className="truncate">{status}</span><span className="shrink-0 ml-1">{items.length}</span>
      </header>
      <div className="p-1.5 space-y-1.5 min-h-[100px] max-h-[70vh] overflow-y-auto">
        {items.map((p) => (
          <ProjectCard key={p.id} p={p} canBulkDelete={canBulkDelete} selected={selected.has(p.id)} onToggleSelect={onToggleSelect} onOpen={onOpen} prefetchProps={prefetchProps} />
        ))}
        {footer}
      </div>
    </div>
  );
}

export function Projects() {
  const { hasScope, user } = useAuth();
  const navigate = useNavigate();
  const canBulkDelete = ["founder", "partner"].includes(user?.role);
  const invalidatePortal = usePortalInvalidate();
  const prefetchProject = usePrefetchDetailOnHover("project");
  // Wave 1 (2026-10, frontend data cache) point 4 — view/archived/search live in the URL so
  // Back (and a refresh, or a shared link) restores the exact same board a PM was looking at.
  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode = ["active", "all", "pm_missing"].includes(searchParams.get("view")) ? searchParams.get("view") : "active"; // "active" | "all" | "pm_missing"
  const includeArchived = searchParams.get("archived") === "1";
  const query = searchParams.get("q") || "";
  const setViewMode = (v) => setSearchParams((prev) => { const n = new URLSearchParams(prev); v === "active" ? n.delete("view") : n.set("view", v); return n; });
  const setIncludeArchived = (v) => setSearchParams((prev) => { const n = new URLSearchParams(prev); v ? n.set("archived", "1") : n.delete("archived"); return n; });
  const setQuery = (v) => setSearchParams((prev) => { const n = new URLSearchParams(prev); v ? n.set("q", v) : n.delete("q"); return n; }, { replace: true });
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [searchResults, setSearchResults] = useState(null); // null = not searching

  const { data: statuses = [] } = useProjectStatusesQuery();

  // Active-first board (fast, lightweight — default view) + "All" (opt-in, unchanged
  // behaviour) + "PM Missing" — each is its own cached query (60s staleTime, Notion-synced
  // data), only the currently-active tab's query actually fetches; switching tabs shows any
  // previously-cached one instantly while a background refresh checks for changes.
  const [expanded, setExpanded] = useState({}); // { Delivered: { items, page, total, hasMore, loading } }
  const canAssignPm = hasScope("projects.create");

  const boardQ = useProjectsBoardQuery(viewMode === "active");
  const allQ = useProjectsAllQuery(includeArchived, viewMode === "all");
  // PM Missing is ALSO always kept warm (enabled unconditionally) — its `total_count` feeds
  // the tab's own badge regardless of which tab is actually open.
  const pmMissingQ = useProjectsPmMissingQuery(canAssignPm, true);

  const activeItems = useMemo(() => boardQ.data?.active || [], [boardQ.data?.active]);
  const deferredCounts = boardQ.data?.deferred_counts || {};
  const allItems = useMemo(() => allQ.data?.items || [], [allQ.data?.items]);
  const pmMissingItems = pmMissingQ.data?.items || [];
  const pmMissingCount = pmMissingQ.data?.total_count ?? null;
  const pmEligible = pmMissingQ.data?.eligible || [];
  const [assigningId, setAssigningId] = useState(null);

  const activeQuery = viewMode === "active" ? boardQ : viewMode === "all" ? allQ : pmMissingQ;
  const loading = activeQuery.isLoading;
  // Perf fix (2026-10, Cursor audit item 9 / group 0C) behaviour preserved: a failed refresh
  // with nothing on screen yet shows the Retry empty-state; once anything has loaded, a later
  // failed background refresh no longer blanks the page (Wave 1 point 3).
  const loadError = activeQuery.isError && !activeQuery.data;
  const refresh = () => activeQuery.refetch();

  const assignPm = async (projectId, pmUserId) => {
    setAssigningId(projectId);
    try {
      await api.patch(`/v1/projects/${projectId}`, { pm_user_id: pmUserId });
      invalidatePortal.project(projectId);
      toast.success("Project Manager assigned");
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      toast.error(code === "invalid_pm" ? "That person can't be assigned as Project Manager" : "Couldn't assign the Project Manager");
    } finally {
      setAssigningId(null);
    }
  };

  useEffect(() => {
    setExpanded({});
    setSelected(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, includeArchived]);

  // C (Batch Item 2): global server-side search across ALL projects, regardless of column or
  // pagination — reuses the existing /search endpoint (already scans the full workspace, not
  // just what's currently rendered).
  useEffect(() => {
    const q = query.trim();
    if (!q) { setSearchResults(null); return; }
    const t = setTimeout(() => {
      api.get("/v1/search/", { params: { q } }).then((r) => setSearchResults(r.data.groups?.projects || []));
    }, 200);
    return () => clearTimeout(t);
  }, [query]);

  const loadMoreDeferred = async (status) => {
    const current = expanded[status] || { items: [], page: 0 };
    setExpanded((prev) => ({ ...prev, [status]: { ...current, loading: true } }));
    const nextPage = current.page + 1;
    const r = await api.get("/v1/projects/board/lazy", { params: { status, page: nextPage, page_size: 20, include_archived: includeArchived } });
    setExpanded((prev) => ({
      ...prev,
      [status]: { items: [...current.items, ...(r.data.items || [])], page: nextPage, total: r.data.total, hasMore: r.data.has_more, loading: false },
    }));
  };
  const collapseDeferred = (status) => setExpanded((prev) => { const n = { ...prev }; delete n[status]; return n; });

  // Active view: columns from Notion statuses, minus the two deferred ones, over the lightweight board payload.
  const activeColumns = (statuses.length ? statuses : Array.from(new Set(activeItems.map((i) => i.status).filter(Boolean)))).filter((s) => !DEFERRED_STATUSES.includes(s));
  const activeByStatus = activeColumns.map((s) => ({ status: s, items: activeItems.filter((i) => i.status === s) }));
  const unassignedActive = activeItems.filter((i) => !i.status);
  if (unassignedActive.length) activeByStatus.push({ status: "Unassigned", items: unassignedActive });

  // "All" view: unchanged original behaviour — every status, fully loaded, no collapsing.
  const allColumns = statuses.length ? statuses : Array.from(new Set(allItems.map((i) => i.status).filter(Boolean)));
  const allByStatus = allColumns.map((s) => ({ status: s, items: allItems.filter((i) => i.status === s) }));
  const unassignedAll = allItems.filter((i) => !i.status);
  if (unassignedAll.length) allByStatus.push({ status: "Unassigned", items: unassignedAll });

  const visibleIds = useMemo(() => {
    if (searchResults) return searchResults.map((i) => i.id);
    if (viewMode === "all") return allItems.map((i) => i.id);
    return [...activeItems.map((i) => i.id), ...Object.values(expanded).flatMap((e) => e.items.map((i) => i.id))];
  }, [searchResults, viewMode, allItems, activeItems, expanded]);

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelected(new Set(visibleIds));
  const clearSelection = () => setSelected(new Set());
  const openProject = (id) => (selected.size > 0 ? toggleSelect(id, { stopPropagation: () => {} }) : navigate(`/projects/${id}`));

  // A (2026-02): founder/partner bulk delete — count-based confirm (not the stricter
  // typed-exact-name single purge), with a warning if any selected project has linked
  // Quotes/Documents, same pattern as the Documents bulk-delete.
  const bulkDelete = async () => {
    if (selected.size === 0) return;
    setDeleting(true);
    try {
      const ids = Array.from(selected);
      const preview = await api.get(`/v1/projects/bulk-delete-preview?ids=${ids.join(",")}`);
      const withLinks = Object.entries(preview.data.items || {}).filter(([, v]) => v.has_links);
      let message = `Delete ${selected.size} project(s)? This cannot be undone.`;
      if (withLinks.length) {
        message += `\n\nWarning: ${withLinks.length} of them have linked Quotes and/or Documents (SOWs) — those linked records will NOT be deleted, only the project itself.`;
      }
      if (!window.confirm(message)) { setDeleting(false); return; }
      const r = await api.post("/v1/projects/bulk-delete", { ids });
      toast.success(`${r.data.deleted.length} project(s) deleted`);
      if (r.data.failed?.length) toast.error(`${r.data.failed.length} project(s) failed to delete`);
      clearSelection();
      invalidatePortal.projectLists();
      invalidatePortal.removeProjectDetails(r.data.deleted);
      invalidatePortal.clientsAll();
    } catch {
      toast.error("Couldn't delete projects");
    } finally {
      setDeleting(false);
    }
  };

  const totalVisible = viewMode === "active" ? activeItems.length : viewMode === "pm_missing" ? pmMissingItems.length : allItems.length;

  return (
    <div className="p-8" data-testid="projects-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-editorial text-4xl tracking-tight text-ink">Projects</h1>
        <div className="flex items-center gap-4 flex-wrap">
          {canBulkDelete && selected.size > 0 && (
            <div className="flex items-center gap-2" data-testid="projects-bulk-bar">
              <span className="text-xs font-medium text-gold-text">{selected.size} selected</span>
              <button onClick={clearSelection} className="text-xs text-faint hover:text-ink">Clear</button>
              <button data-testid="projects-bulk-delete-btn" onClick={bulkDelete} disabled={deleting}
                className="inline-flex items-center gap-1.5 rounded-md border border-tone-red-text/30 bg-tone-red-bg px-3 py-1.5 text-xs font-medium text-tone-red-text hover:bg-tone-red-bg/70 disabled:opacity-50">
                {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Delete Selected ({selected.size})
              </button>
            </div>
          )}
          {canBulkDelete && !searchResults && viewMode !== "pm_missing" && totalVisible > 0 && (
            <button data-testid="projects-select-all-btn" onClick={selected.size === visibleIds.length ? clearSelection : selectAll}
              className="text-xs text-ink-soft hover:text-ink">
              {selected.size === visibleIds.length ? "Deselect all" : "Select all"}
            </button>
          )}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-faint" />
            <input data-testid="projects-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search all projects…"
              className="w-56 rounded-md border border-hairline bg-paper pl-8 pr-3 py-1.5 text-xs text-ink placeholder:text-faint focus:outline-none focus:border-brand" />
          </div>
          <div className="inline-flex rounded-md border border-hairline p-0.5 bg-canvas">
            <button data-testid="projects-view-active" onClick={() => setViewMode("active")}
              className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${viewMode === "active" ? "bg-paper text-ink shadow-sm" : "text-faint hover:text-ink"}`}>Active</button>
            <button data-testid="projects-view-all" onClick={() => setViewMode("all")}
              className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${viewMode === "all" ? "bg-paper text-ink shadow-sm" : "text-faint hover:text-ink"}`}>All</button>
            <button data-testid="projects-view-pm-missing" onClick={() => setViewMode("pm_missing")}
              className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${viewMode === "pm_missing" ? "bg-paper text-ink shadow-sm" : "text-faint hover:text-ink"}`}>
              PM Missing{pmMissingCount != null && pmMissingCount > 0 ? ` (${pmMissingCount})` : ""}
            </button>
          </div>
          <label className="inline-flex items-center gap-2 text-xs text-ink-soft">
            <Switch checked={includeArchived} onCheckedChange={setIncludeArchived} data-testid="projects-show-archived-toggle" />
            Show archived
          </label>
          {hasScope("projects.create") && (
            <button data-testid="new-project-btn" onClick={() => setShowNew(true)}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-ink hover:bg-brand-dim">
              <Plus className="h-3.5 w-3.5" /> New Project
            </button>
          )}
        </div>
      </div>
      <RecordFormModal type="project" open={showNew} onClose={() => setShowNew(false)}
        onCreated={(record, opts) => { invalidatePortal.projectLists(); if (opts?.openDetail && record?.id) navigate(`/projects/${record.id}`); }} />

      {activeQuery.isError && totalVisible > 0 && (
        <div className="mb-3 flex items-center justify-between rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="projects-refresh-error">
          <span>Showing a saved copy — unable to refresh just now.</span>
          <button onClick={refresh} data-testid="projects-refresh-retry" className="font-semibold underline">Retry</button>
        </div>
      )}
      {searchResults ? (
        <div data-testid="projects-search-results">
          <div className="text-xs text-faint mb-3">{searchResults.length} project(s) match "{query}" — searched across the entire workspace, not just what's loaded.</div>
          {searchResults.length === 0 ? (
            <div className="rounded-lg border border-hairline bg-paper"><EmptyState title="No projects match your search" hint="Try a different name, client, or status." testId="projects-search-empty" /></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
              {searchResults.map((p) => (
                <ProjectCard key={p.id} p={p} canBulkDelete={canBulkDelete} selected={selected.has(p.id)} onToggleSelect={toggleSelect} onOpen={openProject} prefetchProps={prefetchProject} />
              ))}
            </div>
          )}
        </div>
      ) : loadError && totalVisible === 0 ? (
        // Perf fix (2026-10, Cursor audit item 9 / group 0C): loadBoard/loadAll/loadPmMissing
        // used to have no .catch — a failed request left `loading` true forever (permanent
        // skeleton). Retry re-runs the same loader; any content already on screen is left
        // alone (this branch only replaces the empty-state case, never wipes existing rows).
        <div className="rounded-lg border border-hairline bg-paper p-8 text-center space-y-3" data-testid="projects-load-error">
          <p className="text-sm text-faint">Couldn't load projects.</p>
          <button onClick={refresh} data-testid="projects-retry-btn"
            className="inline-flex items-center rounded border border-hairline bg-paper px-3.5 py-2 text-[12.5px] font-semibold text-ink-soft hover:border-brand">
            Retry
          </button>
        </div>
      ) : loading ? <CardGridSkeleton count={9} testId="projects-skeleton" /> : viewMode !== "pm_missing" && totalVisible === 0 ? (
        <div className="rounded-lg border border-hairline bg-paper">
          <EmptyState title="No projects yet" hint="Add a project to start tracking it here." testId="projects-empty"
            actionLabel={hasScope("projects.create") ? "+ New Project" : undefined} onAction={hasScope("projects.create") ? () => setShowNew(true) : undefined} />
        </div>
      ) : viewMode === "active" ? (
        // Phase 4 (2026-10, Design System 2.0 visual reskin) — ONE horizontal row of 232px
        // pipeline columns (horizontal scroll, never wraps); Delivered/Archived collapse to a
        // narrow 168px column until expanded, then widen to the same 232px as every other
        // column so their cards have room.
        <div className="flex gap-3 overflow-x-auto pb-2" data-testid="projects-board-active">
          {activeByStatus.map((col) => (
            <ProjectColumn key={col.status} status={col.status} items={col.items} canBulkDelete={canBulkDelete} selected={selected} onToggleSelect={toggleSelect} onOpen={openProject} prefetchProps={prefetchProject} />
          ))}
          {DEFERRED_STATUSES.map((status) => {
            const count = deferredCounts[status] || 0;
            const ex = expanded[status];
            return (
              <div key={status} className="shrink-0 rounded-md border border-hairline bg-paper" style={{ width: ex ? 232 : 168 }} data-testid={`project-column-deferred-${status}`}>
                <header className="border-b border-hairline px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-faint flex justify-between items-center">
                  <span className="truncate">{status}</span>
                  <span className="flex items-center gap-2 shrink-0 ml-1">
                    {count}
                    {ex && (
                      <button data-testid={`project-column-collapse-${status}`} onClick={() => collapseDeferred(status)} className="text-faint hover:text-ink normal-case">Collapse</button>
                    )}
                  </span>
                </header>
                <div className="p-1.5 space-y-1.5 min-h-[100px] max-h-[70vh] overflow-y-auto">
                  {!ex ? (
                    count === 0 ? (
                      <div className="text-xs text-faint text-center py-6">None</div>
                    ) : (
                      <button data-testid={`project-column-expand-${status}`} onClick={() => loadMoreDeferred(status)}
                        className="w-full rounded border border-dashed border-hairline py-4 text-[11px] text-ink-soft hover:border-brand hover:text-ink">
                        Show {count} {status.toLowerCase()}
                      </button>
                    )
                  ) : (
                    <>
                      {ex.items.map((p) => (
                        <ProjectCard key={p.id} p={p} canBulkDelete={canBulkDelete} selected={selected.has(p.id)} onToggleSelect={toggleSelect} onOpen={openProject} prefetchProps={prefetchProject} />
                      ))}
                      {ex.hasMore && (
                        <button data-testid={`project-column-load-more-${status}`} onClick={() => loadMoreDeferred(status)} disabled={ex.loading}
                          className="w-full rounded border border-hairline py-2 text-xs text-ink-soft hover:border-brand hover:text-ink disabled:opacity-50 flex items-center justify-center gap-1.5">
                          {ex.loading ? <Loader2 className="h-3 w-3 animate-spin" /> : null} Load more ({ex.items.length} of {ex.total})
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : viewMode === "pm_missing" ? (
        <div className="rounded-lg border border-hairline bg-paper overflow-x-auto" data-testid="projects-pm-missing-list">
          {pmMissingItems.length === 0 ? (
            <EmptyState title="Every active project has a PM" hint="Nothing needs assigning right now — Delivered projects with no PM would show here too." testId="projects-pm-missing-empty" />
          ) : (
            <table className="w-full min-w-[640px] text-xs">
              <thead className="bg-canvas text-[10px] font-semibold uppercase tracking-wider text-faint">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Project</th>
                  <th className="text-left px-4 py-2 font-medium">Client</th>
                  <th className="text-left px-4 py-2 font-medium">Status</th>
                  <th className="text-left px-4 py-2 font-medium"></th>
                  <th className="text-right px-4 py-2 font-medium">Assign PM</th>
                </tr>
              </thead>
              <tbody>
                {pmMissingItems.map((p) => (
                  <tr key={p.id} className="border-t border-hairline hover:bg-surface2" data-testid={`pm-missing-row-${p.id}`}>
                    <td className="px-4 py-2">
                      <Link to={`/projects/${p.id}`} className="text-ink hover:text-gold-text font-medium">{p.name}</Link>
                    </td>
                    <td className="px-4 py-2 text-ink-soft">{p.client_name || "—"}</td>
                    <td className="px-4 py-2 text-ink-soft">{p.status}</td>
                    <td className="px-4 py-2">
                      <span data-testid={`pm-missing-badge-${p.id}`}
                        className={`rounded-full px-1.5 py-0.5 text-[9px] uppercase tracking-wider ${p.pm_required ? "bg-tone-red-bg text-tone-red-text" : "bg-tone-gold-bg text-tone-gold-text"}`}>
                        {p.pm_required ? "Required" : "Flagged"}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      {canAssignPm ? (
                        <select data-testid={`pm-missing-assign-${p.id}`} value="" disabled={assigningId === p.id}
                          onChange={(e) => e.target.value && assignPm(p.id, e.target.value)}
                          className="rounded-md border border-hairline px-2 py-1 text-xs bg-paper disabled:opacity-50">
                          <option value="">{assigningId === p.id ? "Saving…" : "Assign PM…"}</option>
                          {pmEligible.map((u) => (<option key={u.id} value={u.id}>{u.name}</option>))}
                        </select>
                      ) : (
                        <span className="text-faint">Ask a founder/partner/PM</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-2" data-testid="projects-board-all">
          {allByStatus.map((col) => (
            <ProjectColumn key={col.status} status={col.status} items={col.items} canBulkDelete={canBulkDelete} selected={selected} onToggleSelect={toggleSelect} onOpen={openProject} prefetchProps={prefetchProject} />
          ))}
        </div>
      )}
    </div>
  );
}
